import { List } from "immutable";
import { BOTTOM, LEFT, RIGHT, TOP, CENTER } from "../constants/DnDConstants";
import noOp from "./noOp";

/**
 * typedef imports
 * @typedef {import("react-dnd/dist").DropTargetMonitor} DropTargetMonitor
 * @typedef {import("react-dnd/dist").DragSourceHookSpec} DragSourceHookSpec
 * @typedef {import("react-dnd/dist").DropTargetHookSpec} DropTargetHookSpec
 */

/**
 * Represents the side of of the drop area the drag item should be dropped
 * @typedef {"bottom" | "left" | "right" | "top" | "center"} DropAreaLabel
 */

export const getBorderSide = (dropArea) => (dropArea === TOP ? "borderTop" : "borderBottom");

/**
 *  Uses the dropMonitor to get the currently dragged item and it's client offset.
 *  It compares that to the midpoint of the dropRef to determine what side should be the "target".
 *  Defaults to top/bottom as most dnd areas are vertical. Grid areas use left/right like in the expanded gallery asset view.
 *
 * @param {} monitor
 * @param {HTMLElement} dropRef - Ref to the html element representing the drop area
 * @returns {DropAreaLabel}
 */
export const getTargetArea = (monitor, dropRef, supportsCenterDrop = false) => {
  const { height, left, top, width } = dropRef.getBoundingClientRect();
  const { x = 0, y = 0 } = monitor.getClientOffset();
  const item = monitor.getItem();
  let area = null;

  if (item.gridMode) {
    area = x < left + width / 2 ? LEFT : RIGHT;
  } else if (supportsCenterDrop) {
    area = y < top + height / 3 ? TOP : y > top + (height * 2) / 3 ? BOTTOM : CENTER;
  } else {
    area = y < top + height / 2 ? TOP : BOTTOM;
  }

  return area;
};

/**
 * Props to pass to generateDragSpec
 * @typedef {Object} DragSpecParams
 * @property {string} type - A type describing the dragItem. This will tell the dropArea if this dragItem is something it will accept.
 * @property {Object} item - An object containing item data, to be passed to the drop (or onDrop if using generateDropSpec) callback.
 * @property {(() => void)=} onDrop - To be called after a valid drop is registered for this dragItem, usually passes the result to a reflux action.
 * @property {boolean=} canDrag - Optional property to determine if the item can be dragged. Assumed to be true unless set otherwise.
 */

/**
 * Generates a spec to be provided to useDrag. Abstracts repeated logic away.
 * The collector provides isDragging and dragItemType to useDrag (the drag item) as it's being dragged.
 * onDrop is called within end if a valid drop is registered and the result has dropped = true.
 *
 * @param {DragSpecParams} dragSpecParams
 * @returns {DragSourceHookSpec} - to be passed to useDrag;
 */
export const generateDragSpec = ({ type, item, canDrag, onDrop = noOp }) => ({
  type,
  item,
  canDrag,
  collect: (monitor) => ({
    isDragging: monitor.isDragging(),
    dragItemType: monitor.getItemType(),
  }),
  end: (_, monitor) => {
    if (monitor.didDrop()) {
      const result = monitor.getDropResult();
      result?.dropped && onDrop(result);
    }
  },
});

// todo: type item at some point

/**
 * To be passed to generateDropSpec
 * @typedef {Object} DropSpecParams
 * @prop {(item) => boolean=} canDrop - Optional callback to control if an item can be dropped. This is true by default when undefined assuming the types are valid.
 * @prop {boolean=} disabled - Simple flag to disable drops rather than a callback.
 * @prop {((DropTargetMonitor) => void)=} onHover - called when the dropArea is hovered specifically, as in not a child (in case a child is also configured as a dropArea with different logic).
 * @prop {(item, DropTargetMonitor) => Object} onDrop - called when a valid dragItem is dropped into the dropArea. * Item is what was configured in the dragItem's spec *
 * @prop {(() => void)=} onDidDrop - called if didDrop was called before a drop is registered. Carried over from the non-hook version. No idea when or how this might be called.
 * @prop {string | string[]} type - string or list of types the dropArea will accept. LayerPanelBlock is a good example since elements and site canvases can be dropped into it, or other blocks can be dropped above or below.
 */

/**
 * Generates a spec to be provided to useDrop. Abstracts repeated logic away.
 * Accept is a list of dragItem types the dropArea will accept (prevents things from being dropped where they aren't supposed to be).
 * The result of onDrop is spread into the return value of drop. This is then passed to the dragItem's drop/onDrop.
 * This allows the dropArea to control the data transform, since the same item can be handled differently per dropArea.
 *
 * @param {DropSpecParams} dropSpecParams
 * @returns {DropTargetHookSpec}
 */
export const generateDropSpec = ({
  canDrop,
  disabled,
  type,
  onHover = noOp,
  onDidDrop = noOp,
  onDrop,
}) => ({
  accept: type,
  canDrop: disabled ? () => false : canDrop,
  collect: (monitor) => ({
    isOverCurrent: monitor.isOver({ shallow: true }),
    isOver: monitor.isOver(),
    canDrop: monitor.canDrop(),
  }),
  hover: (_, monitor) => {
    if (monitor.isOver({ shallow: true })) {
      onHover(monitor);
    }
  },
  drop: disabled
    ? undefined
    : (dragItem, monitor) => {
        if (monitor.didDrop()) {
          // If you want, you can check whether some nested
          // target already handled drop
          onDidDrop(dragItem, monitor);
          return;
        }
        if (!onDrop) {
          throw new Error('Drop spec is configured without "onDrop" callback');
        }
        return { dropped: true, ...onDrop(dragItem, monitor) };
      },
});

/**
 * Calculate offset for target index when area is bottom or right.
 * @param {string=} area - The drop area: "top", "bottom", "left", "right"
 * @returns {Number}
 */
export const getAreaOnlyOffset = (area) => ([BOTTOM, RIGHT].includes(area) ? 1 : 0);

/**
 * Checks if one index is positioned before another index.  In context of dragging, true means moving down
 * @param {number} firstIndex - The first index to compare
 * @param {number} secondIndex - The second index to compare
 * @returns {boolean}
 */
export const isIndexBefore = (firstIndex, secondIndex) => firstIndex !== -1 && secondIndex !== -1 && firstIndex < secondIndex;

/**
 * Counts how many items from itemList appear before targetIndex in fullList.
 * @param {Array|List} itemList - Array of items to check
 * @param {Array|List} fullList - Array used to determine each item's index position
 * @param {number} targetIndex - The target index position to count items before
 * @returns {number}
 */
export const countItemsBeforeTarget = (itemList, fullList, targetIndex) => 
  itemList.reduce((count, item) => {
    const itemIndex = fullList.indexOf(item);
    return count + Number(isIndexBefore(itemIndex, targetIndex));
  }, 0);

/**
 * Shifts the index for an item up or down (left/right in grid mode) to account for far side in relation to the drop area.
 * @param {Object} params
 * @param {string=} params.area - The drop area: "top", "bottom", "left", "right" for area-only offset
 * @param {number=} params.dragIndex - The index of the item being dragged
 * @param {number=} params.dropIndex - The index of the drop target
 * @example Returns 1 for bottom/right drops (to place after target), 0 for top/left drops
 * @example When moving down (dragIndex < dropIndex), subtracts 1 because the item is removed first, shifting
 *   all subsequent indices down by 1. When moving up (dragIndex > dropIndex), no adjustment is needed.
 * @returns {Number}
 */
export const getDropIndexOffset = ({ area, dragIndex, dropIndex }) => (
  getAreaOnlyOffset(area) + // The drop zone of an element down/right adds 1 because the item is added after the target
  (isIndexBefore(dragIndex, dropIndex) ? -1 : 0) // move down subtract 1 because the item is removed first
);

export default {
  generateDragSpec,
  generateDropSpec,
  getBorderSide,
  getTargetArea,
  getAreaOnlyOffset,
  isIndexBefore,
  countItemsBeforeTarget,
};
